import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Space,
  Button,
  Typography,
  message,
  Tabs,
  Drawer,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { staffService, CreateStaffData } from '../services/staffs';
import { teamService, Team, CreateTeamData } from '../services/teams';
import { roleService, Role } from '../services/roles';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const { Title } = Typography;
const { TabPane } = Tabs;

export const StaffFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<CreateStaffData>();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'staff.update');
  const canDelete = hasPermission(user, 'staff.delete');
  const canManagePassword = user?.roles?.includes('admin') || user?.roles?.includes('owner');
  const [submitting, setSubmitting] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [staffName, setStaffName] = useState<string>('');
  const [staffType, setStaffType] = useState<'active' | 'inactive'>('active');
  const [isEditing, setIsEditing] = useState(!isEdit);
  const [teams, setTeams] = useState<Team[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [teamDrawerVisible, setTeamDrawerVisible] = useState(false);
  const [teamForm] = Form.useForm<CreateTeamData>();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTeams();
    fetchRoles();
    if (isEdit && id) {
      loadStaff(parseInt(id, 10));
    } else {
      form.setFieldsValue({ type: 'active' });
      setStaffType('active');
    }
  }, [isEdit, id, form]);

  const fetchTeams = async () => {
    try {
      const response = await teamService.getAll();
      setTeams(response.data || []);
    } catch {
      message.error('Failed to load teams');
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesList = await roleService.getAll();
      const allowedRoles = ['junior', 'senior', 'manager', 'admin'];
      const filtered = rolesList.filter((role) => allowedRoles.includes(role.name.toLowerCase()));
      setRoles(filtered);
    } catch {
      message.error('Failed to load roles');
    }
  };

  const loadStaff = async (staffId: number) => {
    setLoadingStaff(true);
    try {
      const staff = await staffService.getById(staffId);
      setStaffName(staff.name);
      setStaffType(staff.type || 'active');
      form.setFieldsValue({
        ...staff,
      } as any);
    } catch {
      message.error('Failed to load staff');
      navigate('/staffs');
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this staff?')) return;
    
    try {
      await staffService.delete(parseInt(id, 10));
      message.success('Staff deleted');
      navigate('/staffs');
    } catch {
      message.error('Failed to delete staff');
    }
  };

  const handleTypeChange = async (newType: 'active' | 'inactive') => {
    setStaffType(newType);
    form.setFieldValue('type', newType);
    
    // If in edit mode, save immediately
    if (isEdit && id) {
      try {
        await staffService.update(parseInt(id, 10), { type: newType });
        message.success('Staff type updated');
      } catch {
        message.error('Failed to update staff type');
        // Revert on error
        const currentType = form.getFieldValue('type') || 'active';
        setStaffType(currentType);
      }
    }
  };

  const handleSubmit = async (values: CreateStaffData) => {
    setSubmitting(true);
    try {
      const submitData = { ...values };
      
      // In edit mode, if password is empty, don't send it (to keep current password)
      if (isEdit && (!submitData.password || submitData.password.trim() === '')) {
        delete submitData.password;
      }
      if (!canManagePassword) {
        delete submitData.password;
      }
      
      if (isEdit && id) {
        await staffService.update(parseInt(id, 10), submitData);
        message.success('Staff updated');
        setIsEditing(false);
        // Reload staff data
        await loadStaff(parseInt(id, 10));
      } else {
        await staffService.create(submitData);
        message.success('Staff created');
        navigate('/staffs');
      }
    } catch {
      message.error(isEdit ? 'Failed to update staff' : 'Failed to create staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTeam = async (values: CreateTeamData) => {
    try {
      const newTeam = await teamService.create(values);
      message.success('Team created successfully');
      setTeamDrawerVisible(false);
      teamForm.resetFields();
      // Refresh teams list
      await fetchTeams();
      // Set the newly created team in the form
      form.setFieldValue('team_id', newTeam.id);
    } catch {
      message.error('Failed to create team');
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {isEdit && staffName && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {staffName}
          </Title>
          <Space>
            {!isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      )}
      
      {!isEdit && (
      <Title level={3} style={{ marginBottom: 24 }}>
          New Staff
      </Title>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ type: 'active' }}
        disabled={loadingStaff || (isEdit && !isEditing)}
        onValuesChange={(changedValues, allValues) => {
          // Update staff name when name field changes
          if (changedValues.name !== undefined) {
            setStaffName(changedValues.name);
          }
          // Update staff type when type field changes
          if (changedValues.type !== undefined) {
            setStaffType(changedValues.type);
          }
        }}
      >
        <Tabs 
          defaultActiveKey="overview"
          tabBarExtraContent={
            isEdit ? (
          <Space size={8}>
            <div
              onClick={() => handleTypeChange('active')}
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: staffType === 'active' ? '#E6F7FF' : '#f5f5f5',
                border: staffType === 'active' ? '1px solid #40A9FF' : '1px solid #d9d9d9',
                color: staffType === 'active' ? '#1890ff' : '#595959',
                fontWeight: staffType === 'active' ? 500 : 400,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s',
              }}
            >
              <span style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: staffType === 'active' ? '#1890ff' : '#8c8c8c',
              }}></span>
              Active
            </div>
            <div
              onClick={() => handleTypeChange('inactive')}
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: staffType === 'inactive' ? '#FFF1F0' : '#f5f5f5',
                border: staffType === 'inactive' ? '1px solid #FF7875' : '1px solid #d9d9d9',
                color: staffType === 'inactive' ? '#ff4d4f' : '#595959',
                fontWeight: staffType === 'inactive' ? 500 : 400,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s',
              }}
            >
              <span style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: staffType === 'inactive' ? '#ff4d4f' : '#8c8c8c',
              }}></span>
              Inactive
            </div>
          </Space>
            ) : null
          }
        >
          <TabPane tab="Overview" key="overview">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Name</strong>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item
                          name="name"
                          rules={[{ required: true, message: 'Please enter name' }]}
                          style={{ margin: 0 }}
                        >
                          <Input placeholder="Enter staff name" />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('name') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Team</strong>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item
                          name="team_id"
                          style={{ margin: 0 }}
                        >
                          <Select
                            placeholder="Select team"
                            allowClear
                            dropdownRender={(menu) => (
                              <>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                                  <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => setTeamDrawerVisible(true)}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  >
                                    Add Team
                                  </Button>
                                </div>
                              </>
                            )}
                          >
                            {teams.map(team => (
                              <Select.Option key={team.id} value={team.id}>
                                {team.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>{teams.find(t => t.id === form.getFieldValue('team_id'))?.name || '-'}</div>
                      )}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Role</strong>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item
                          name="role_id"
                          style={{ margin: 0 }}
                          rules={[{ required: true, message: 'Please select role' }]}
                        >
                          <Select placeholder="Select role" allowClear>
                            {roles.map(role => (
                              <Select.Option key={role.id} value={role.id}>
                                {role.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>{roles.find(r => r.id === form.getFieldValue('role_id'))?.name || '-'}</div>
                      )}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Contact No.</strong>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item
                          name="contact_number"
                          style={{ margin: 0 }}
                        >
                          <Input placeholder="Enter contact no." />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('contact_number') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Email</strong>
                    </td>
                    <td style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item
                          name="email"
                          rules={[
                            { required: true, message: 'Please enter email' },
                            { type: 'email', message: 'Please enter a valid email' },
                          ]}
                          style={{ margin: 0 }}
                        >
                          <Input placeholder="Enter email" type="email" />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('email') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  {canManagePassword && (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                        <strong>Password</strong>
                      </td>
                      <td style={{ padding: '12px', verticalAlign: 'top' }}>
                        {isEditing ? (
                          <Form.Item
                            name="password"
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!isEdit && !value) {
                                    return Promise.reject(new Error('Please enter password'));
                                  }
                                  if (value && value.length < 6) {
                                    return Promise.reject(new Error('Password must be at least 6 characters'));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                            style={{ margin: 0 }}
                          >
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                              <Input.Password
                                placeholder={isEdit ? "Leave blank to keep current password" : "Enter password"}
                              />
                              <Button
                                onClick={() => {
                                  form.setFieldsValue({ password: '' });
                                }}
                              >
                                Reset Password
                              </Button>
                            </div>
                          </Form.Item>
                        ) : (
                          <div>••••••</div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabPane>
        </Tabs>
      </Form>

      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 24,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        
      }}>
        <Space>
          {isEditing && (
            <>
              <Button onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload staff data to reset form
                  loadStaff(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/staffs');
                }
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          {!isEditing && (
            <Button onClick={() => navigate('/staffs')}>Cancel</Button>
          )}
        </Space>
      </div>

      <Drawer
        title="Add Team"
        placement="right"
        onClose={() => {
          setTeamDrawerVisible(false);
          teamForm.resetFields();
        }}
        open={teamDrawerVisible}
        width={400}
      >
        <Form
          form={teamForm}
          layout="vertical"
          onFinish={handleCreateTeam}
        >
          <Form.Item
            name="name"
            label="Team Name"
            rules={[{ required: true, message: 'Please enter team name' }]}
          >
            <Input placeholder="Enter team name" />
          </Form.Item>
          <Form.Item
            name="contact_number"
            label="Contact Number"
          >
            <Input placeholder="Enter contact number" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email" type="email" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
              <Button onClick={() => {
                setTeamDrawerVisible(false);
                teamForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};
